/**
 * Persisted onboarding-account OAuth return URL (CLA-868).
 *
 * On the onboarding account step, "Continue with Google" starts a Clerk sign-up
 * redirect whose `redirectUrlComplete` carries the invite token + email back to
 * the account step. That works for a FRESH sign-up — but when the Google
 * identity ALREADY exists in the Clerk instance, Clerk performs a sign-up→sign-in
 * TRANSFER, and the transfer path does NOT honor `redirectUrlComplete`. It falls
 * back to the callback component's fallback props, which — un-set — default to
 * the app home, dropping the invite context entirely.
 *
 * This module persists the intended return URL in `sessionStorage` just before
 * the browser leaves for Google, so the ClawInstall sso-callback can resolve it
 * into explicit `signInFallbackRedirectUrl` / `signUpFallbackRedirectUrl` props
 * and land the transfer case back on the account step. It is consumed (and
 * cleared) once, so a later unrelated login never inherits a stale redirect.
 *
 * Kept free of atom / RPC imports (mirrors `connect-callback.ts`). SSR/jsdom-safe:
 * every `window` access is guarded.
 *
 * @since 1.0.0
 */

/** The `sessionStorage` key carrying the onboarding-account return URL. */
export const ONBOARDING_ACCOUNT_RETURN_KEY = "clawinstall:onboarding:account-return";

/** Minimal shape of the browser globals these helpers read. */
export interface OnboardingReturnWindowLike {
  readonly sessionStorage?: {
    readonly getItem: (key: string) => string | null;
    readonly setItem: (key: string, value: string) => void;
    readonly removeItem: (key: string) => void;
  };
}

/**
 * Pure: the return URL an in-flight onboarding-account OAuth round-trip should
 * land on, or `null` when absent (`null` env = SSR → null). Only a same-origin
 * app path (leading `/`) is honored, so a stale or tampered absolute URL can
 * never redirect the browser off-site. Exported for testing.
 *
 * @since 1.0.0
 */
export const readOnboardingAccountReturn = (win: OnboardingReturnWindowLike | null): string | null => {
  if (win === null) return null;
  const value = win.sessionStorage?.getItem(ONBOARDING_ACCOUNT_RETURN_KEY) ?? null;
  return value !== null && value.startsWith("/") ? value : null;
};

/**
 * Resolve {@link readOnboardingAccountReturn} against the real `window` (or SSR).
 *
 * @since 1.0.0
 */
export const onboardingAccountReturn = (): string | null =>
  typeof window === "undefined" ? null : readOnboardingAccountReturn(window as OnboardingReturnWindowLike);

/**
 * Persist the onboarding-account OAuth return URL just before leaving for the
 * provider. No-op during SSR.
 *
 * @since 1.0.0
 */
export const markOnboardingAccountReturn = (returnUrl: string): void => {
  if (typeof window === "undefined") return;
  (window as OnboardingReturnWindowLike).sessionStorage?.setItem(ONBOARDING_ACCOUNT_RETURN_KEY, returnUrl);
};

/**
 * Clear the onboarding-account return marker (once the callback has consumed
 * it). No-op during SSR.
 *
 * @since 1.0.0
 */
export const clearOnboardingAccountReturn = (): void => {
  if (typeof window === "undefined") return;
  (window as OnboardingReturnWindowLike).sessionStorage?.removeItem(ONBOARDING_ACCOUNT_RETURN_KEY);
};
